import { COMPANY } from './config';

// Shared "last updated" date for the legal pages below. Bump this whenever
// either policy's content changes.
export const LEGAL_EFFECTIVE_DATE = 'August 5, 2026';

// Grievance contact required for Indian websites under the IT (Intermediary
// Guidelines and Digital Media Ethics Code) Rules, 2021 — kept in one place
// so both legal pages and the footer can reference the same details.
export const GRIEVANCE_OFFICER = {
  name: COMPANY.founder,
  designation: 'Founder & Managing Director',
  email: COMPANY.email,
  phone: COMPANY.phone,
  address: COMPANY.registeredAddress,
  responseWindow: '15 days',
};

export const VULNERABILITY_DISCLOSURE_POLICY = {
  title: 'Vulnerability Disclosure Policy',
  eyebrow: 'Security',
  effectiveDate: LEGAL_EFFECTIVE_DATE,
  intro:
    `${COMPANY.name} ("TowerEdge", "we", "us", or "our") takes the security of our website, systems, and client data seriously. This policy explains how to responsibly report a security vulnerability you discover in our digital assets, and what you can expect from us in return. It is written to align with the Information Technology Act, 2000 (as amended) and the directions issued by the Indian Computer Emergency Response Team (CERT-In) under Section 70B of that Act.`,
  sections: [
    {
      heading: 'Our Commitment',
      paragraphs: [
        'We welcome reports from security researchers and members of the public who discover a genuine vulnerability in our systems and disclose it to us responsibly and in good faith. We will acknowledge your report, investigate it promptly, and keep you informed of our progress toward a resolution.',
      ],
    },
    {
      heading: 'Scope',
      paragraphs: [
        'This policy applies to the following, unless expressly excluded below:',
      ],
      list: [
        'The TowerEdge Engineering Consultants public website and its contact/enquiry forms.',
        'Any subdomain or web application that TowerEdge operates and controls directly.',
      ],
      trailingParagraphs: [
        'The following are out of scope: third-party services we merely link to or embed (including our email/form-relay and hosting providers), denial-of-service (DoS/DDoS) testing, physical security testing of our offices, social engineering or phishing of our staff or clients, and automated vulnerability scanning that generates significant traffic without prior written permission.',
      ],
    },
    {
      heading: 'Reporting a Vulnerability',
      paragraphs: [
        `If you believe you have found a security vulnerability, please report it to us at ${COMPANY.email} with the subject line "Security Vulnerability Report". To help us triage and reproduce the issue quickly, please include:`,
      ],
      list: [
        'A clear description of the vulnerability and its potential impact.',
        'Step-by-step instructions to reproduce the issue, including the URL, request, or component affected.',
        'Any proof-of-concept code, screenshots, or logs, provided they do not contain personal or confidential data belonging to others.',
        'Your name and contact details, if you would like to be credited or kept updated.',
      ],
      trailingParagraphs: [
        'Please do not publicly disclose a vulnerability before we have had a reasonable opportunity to investigate and remediate it, and please avoid accessing, modifying, or exfiltrating data that does not belong to you while testing.',
      ],
    },
    {
      heading: 'Our Response Process',
      paragraphs: [
        'We aim to acknowledge new reports within 3 business days and provide an initial assessment within 10 business days. Timelines may vary with the complexity of the issue. Where a report concerns a cybersecurity incident of a type notified by CERT-In, we will additionally comply with the mandatory reporting timelines prescribed under the CERT-In Cyber Security Directions, 2022 (including reporting qualifying incidents to CERT-In within 6 hours of noticing them).',
      ],
    },
    {
      heading: 'Safe Harbor',
      paragraphs: [
        'We will not pursue legal action against researchers who discover and report a vulnerability in good faith, in accordance with this policy, and who do not: access or modify data beyond what is necessary to demonstrate the issue; disrupt or degrade our services; or violate any applicable law, including the Information Technology Act, 2000. This safe harbor does not extend to testing conducted on systems or data outside the scope defined above, or to any activity that is unlawful, negligent, or made with malicious intent.',
      ],
    },
    {
      heading: 'Recognition',
      paragraphs: [
        'We do not currently operate a paid bug bounty program. With your permission, we are glad to publicly credit researchers who submit a valid, responsibly disclosed report that leads to a fix.',
      ],
    },
    {
      heading: 'Contact',
      paragraphs: [
        `For all vulnerability reports and questions about this policy, write to ${COMPANY.email}. For urgent matters, you may also call ${COMPANY.phone}.`,
      ],
    },
  ],
};

export const TERMS_AND_CONDITIONS = {
  title: 'Terms and Conditions',
  eyebrow: 'Legal',
  effectiveDate: LEGAL_EFFECTIVE_DATE,
  intro:
    `These Terms and Conditions ("Terms") govern your access to and use of the ${COMPANY.name} website (the "Site") and any enquiry, contact, or engagement made through it. By accessing or using the Site, you agree to be bound by these Terms, which are made available in accordance with the Information Technology Act, 2000 and the rules made thereunder, including the Information Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021. If you do not agree with these Terms, please discontinue use of the Site.`,
  sections: [
    {
      heading: 'About Us',
      paragraphs: [
        `${COMPANY.fullName} ("TowerEdge", "we", "us", or "our") is a telecom infrastructure engineering firm registered and operating in India, with its registered office at ${COMPANY.registeredAddress}. ${COMPANY.affiliation.statement}`,
      ],
    },
    {
      heading: 'Use of the Site',
      paragraphs: [
        'The Site is provided to share information about our services, projects, and team, and to allow prospective clients and partners to contact us. You agree to use the Site only for lawful purposes and in a manner that does not infringe the rights of, or restrict or inhibit the use and enjoyment of the Site by, any third party.',
      ],
      list: [
        'You will not submit false, misleading, or fraudulent information through our contact or enquiry forms.',
        'You will not attempt to probe, scan, or test the vulnerability of the Site except as permitted under our Vulnerability Disclosure Policy.',
        'You will not use the Site to transmit any unlawful, defamatory, obscene, or infringing material, or any virus, malware, or other harmful code.',
        'You will not attempt to gain unauthorised access to any part of the Site, other accounts, or computer systems connected to the Site.',
      ],
    },
    {
      heading: 'Intellectual Property',
      paragraphs: [
        'All content on the Site — including text, graphics, logos, images, project photography, and the TowerEdge name and marks — is owned by or licensed to us and is protected under the Copyright Act, 1957, the Trade Marks Act, 1999, and other applicable Indian intellectual property laws. You may view and share pages of the Site for personal, non-commercial reference, but you may not reproduce, modify, distribute, or create derivative works from any content without our prior written consent.',
      ],
    },
    {
      heading: 'Enquiries and Communications',
      paragraphs: [
        'Submitting an enquiry through the Site does not create a client, contractor, or other business relationship between you and TowerEdge. Any engagement for our services is subject to a separate, signed agreement setting out its own terms, scope, and conditions, which will govern in the event of any conflict with these Terms.',
      ],
    },
    {
      heading: 'Third-Party Links',
      paragraphs: [
        'The Site may contain links to third-party websites, including those of our affiliate, Ontivity LLC, and our social media and form-processing providers. We do not control and are not responsible for the content, accuracy, or privacy practices of any linked third-party site, and inclusion of a link does not imply endorsement.',
      ],
    },
    {
      heading: 'Disclaimer of Warranties',
      paragraphs: [
        'The Site and its content are provided on an "as is" and "as available" basis. To the fullest extent permitted under the Indian Contract Act, 1872 and other applicable law, we disclaim all warranties, express or implied, regarding the accuracy, completeness, reliability, or availability of the Site or its content, and we do not warrant that the Site will be uninterrupted or error-free.',
      ],
    },
    {
      heading: 'Limitation of Liability',
      paragraphs: [
        'To the extent permitted by applicable Indian law, TowerEdge shall not be liable for any indirect, incidental, special, or consequential loss or damage arising out of or in connection with your access to, or use of, the Site, including loss of data, revenue, or business opportunity, even if we have been advised of the possibility of such loss.',
      ],
    },
    {
      heading: 'Indemnification',
      paragraphs: [
        'You agree to indemnify and hold harmless TowerEdge, its directors, employees, and affiliates from any claim, liability, damage, or expense (including reasonable legal fees) arising from your breach of these Terms or your misuse of the Site.',
      ],
    },
    {
      heading: 'Grievance Redressal',
      paragraphs: [
        `In accordance with the Information Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021, the Grievance Officer for the Site is:`,
      ],
      list: [
        `${GRIEVANCE_OFFICER.name}, ${GRIEVANCE_OFFICER.designation}`,
        `Email: ${GRIEVANCE_OFFICER.email}`,
        `Phone: ${GRIEVANCE_OFFICER.phone}`,
        `Address: ${GRIEVANCE_OFFICER.address}`,
      ],
      trailingParagraphs: [
        `Complaints and grievances regarding content on the Site will be acknowledged and resolved within ${GRIEVANCE_OFFICER.responseWindow} of receipt.`,
      ],
    },
    {
      heading: 'Governing Law and Jurisdiction',
      paragraphs: [
        'These Terms are governed by and construed in accordance with the laws of India. Subject to applicable law, the courts at Visakhapatnam, Andhra Pradesh shall have exclusive jurisdiction over any dispute arising out of or relating to these Terms or your use of the Site.',
      ],
    },
    {
      heading: 'Changes to These Terms',
      paragraphs: [
        'We may update these Terms from time to time to reflect changes in our practices or applicable law. The "Effective Date" above indicates when these Terms were last revised. Continued use of the Site after an update constitutes acceptance of the revised Terms.',
      ],
    },
    {
      heading: 'Contact Us',
      paragraphs: [
        `Questions about these Terms can be sent to ${COMPANY.email} or ${COMPANY.phone}.`,
      ],
    },
  ],
};
